import {
  PageHeader,
  DashboardGrid,
  DashboardCard,
  StatusBadge,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import { CheckCircle2 } from 'lucide-react';
import {
  useCollectionActivities,
  useReminders,
  useCollectionActivityComplete,
} from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { activityStatusVariants, reminderStatusVariants } from '@/config/kosh-status';
import { shortDate } from '@/lib/format';

export function CollectionsPage() {
  const activitiesQ = useCollectionActivities();
  const remindersQ = useReminders();
  const complete = useCollectionActivityComplete();

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Financial"
        title="Collections"
        description="Collection activities and dunning reminders across your receivables."
      />

      <DashboardGrid columns={2}>
        <DashboardCard title="Collection activities" flushBody>
          <QueryStateGuard
            data={activitiesQ.data}
            isLoading={activitiesQ.isLoading}
            isError={activitiesQ.isError}
            emptyTitle="No activities"
            emptyDescription="Plan calls, emails and follow-ups against overdue receivables."
          >
            {(rows) => (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Channel</TableHead>
                    <TableHead>Summary</TableHead>
                    <TableHead>Planned</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.channel}</TableCell>
                      <TableCell className="text-muted-foreground">{a.summary ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{shortDate(a.plannedFor)}</TableCell>
                      <TableCell>
                        <StatusBadge status={a.status} variants={activityStatusVariants} />
                      </TableCell>
                      <TableCell className="text-right">
                        {a.status !== 'DONE' && (
                          <button
                            onClick={() => complete.mutate(a.id)}
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] text-[hsl(142_70%_35%)] hover:bg-muted"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> Done
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </QueryStateGuard>
        </DashboardCard>

        <DashboardCard title="Reminders" flushBody>
          <QueryStateGuard
            data={remindersQ.data}
            isLoading={remindersQ.isLoading}
            isError={remindersQ.isError}
            emptyTitle="No reminders"
            emptyDescription="Dunning reminders generated from your collection ladder appear here."
          >
            {(rows) => (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Channel</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Scheduled</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.channel}</TableCell>
                      <TableCell className="font-mono text-[12px] text-muted-foreground">
                        {r.template ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{shortDate(r.scheduledFor)}</TableCell>
                      <TableCell>
                        <StatusBadge status={r.status} variants={reminderStatusVariants} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </QueryStateGuard>
        </DashboardCard>
      </DashboardGrid>
    </div>
  );
}
